import { ApiError } from './apiError';
import { isTimeoutError } from './requestTimeout';

/**
 * Retry policy for authenticated read queries (see `useApiQuery`).
 *
 * Client errors (4xx) are deterministic — a 400/401/403/404/409/422/429 will
 * not heal on retry, so retrying only delays the caller's error handling (e.g.
 * the project-not-found redirect) and doubles the doomed request. Retry once for
 * everything else: transient network failures (a friendly plain `Error` thrown
 * by `fetchApi`) and 5xx server blips.
 *
 * A request that hit its OWN deadline is not retried either. With `retry: 1` a
 * caller that bounded a screen at 15s would actually sit there for 30s plus
 * backoff, which is the unbounded wait the bound was added to remove (GitHub
 * issue #75 names this interaction). A caller that genuinely wants a second
 * attempt widens its own `timeoutMs`, which is visible at the call site instead
 * of hidden in the retry default.
 *
 * Signature matches TanStack Query's `retry: (failureCount, error) => boolean`.
 */
export function shouldRetryApiQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    return false;
  }
  if (isTimeoutError(error)) {
    return false;
  }
  return failureCount < 1;
}
